/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 04/09/2025 - 05:33:42
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 04/09/2025
    * - Author          : USER
    * - Modification    : 
**/
// src/pages/Home.js
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaGraduationCap,
  FaGlobe,
  FaUsers,
  FaTrophy,
  FaRocket,
  FaStar,
  FaQuoteLeft,
  FaPlay,
  FaChevronRight,
  FaBookOpen,
  FaMicrophone,
  FaLaptopCode,
  FaHandshake,
  FaCalendarAlt,
  FaImages,
  FaCode,
  FaChalkboardTeacher,
  FaLaptop,
  FaBrain
} from 'react-icons/fa';
import { useUser } from '@clerk/clerk-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import assets from '../assets';
import { eventsAPI, adminAPI, fetchSponsors } from '../services/api';
import { isEventPast } from '../utils/eventDate';
import toast from 'react-hot-toast';
import { 
  oxford, 
  learning, 
  unihawk, 
  toloita,
  saySomethingOnMic,
  weGonnaWinThis,
  champions,
  iGotThis,
  thatsTheStage,
  trophies,
  congrats
} from '../assets';


const Home = () => {
  const { isSignedIn, isLoaded } = useUser();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const [mounted, setMounted] = useState(false);

  // Parallax effects
  const y1 = useTransform(scrollY, [0, 300], [0, -50]);
  const y2 = useTransform(scrollY, [0, 300], [0, 50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0.8]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection y1={y1} y2={y2} opacity={opacity} isSignedIn={isSignedIn} navigate={navigate} />

      {/* Stats Section */}
      <StatsSection />

      {/* Features Section */}
      <FeaturesSection navigate={navigate} isSignedIn={isSignedIn} />

      {/* Upcoming Events Section */}
      <UpcomingEventsSection />

      {/* Gallery Section */}
      <GallerySection />

  {/* Sponsors / Partners Section moved to top per design */}

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* CTA Section */}
      <CTASection isSignedIn={isSignedIn} navigate={navigate} />
    </div>
  );
};

// Hero Section Component - African Theme with Slideshow
const HeroSection = ({ isSignedIn, navigate }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Hero slides with content and Eagles Club information
  const heroSlides = [
    {
      src: assets.gallery?.competitions?.[1] || '/api/placeholder/800/600',
      alt: 'Children engaged in passionate debate',
      title: 'Future Leaders in Action',
      subtitle: 'Shape the Future Through Debate & Dialogue',
      description: 'Join Africa\'s premier debate platform where young voices become tomorrow\'s leaders. From local communities to global stages.',
      ctaText: 'Join Us',
      ctaLink: '/register'
    },
    {
      src: weGonnaWinThis,
      alt: 'Eagles Club Premium Training',
      title: 'Eagles Club - Elite Training Program',
      subtitle: 'Exclusive Premium Training for Future World Changers',
      description: 'Eagles Club offers all three ACDC programs tailor-made for elite students passionate about changing the world through words, ideas, creativity, leadership and technology. Boot camp trainings during school holidays with international opportunities at subsidized fees.',
      ctaText: 'Join Eagles Club',
      ctaLink: '/programs'
    },
    {
      src: champions,
      alt: 'International opportunities and achievements',
      title: 'Building Global Champions',
      subtitle: 'International Exposure & Premier Events',
      description: 'Our students participate in locally organized premier events as well as international competitions. Join a community that celebrates excellence and opens doors to global opportunities.',
      ctaText: 'View Programs',
      ctaLink: '/programs'
    }
  ];

  // Auto-slide every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlideIndex(prev => (prev + 1) % heroSlides.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const currentSlide = heroSlides[currentSlideIndex];

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center justify-center pt-20 pb-16 mb-12 overflow-hidden bg-white"
    >
      {/* African Pattern Background */}
      <div
        className="absolute inset-0 opacity-3"
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 20%, #22c55e 2px, transparent 2px),
            radial-gradient(circle at 80% 80%, #fbbf24 2px, transparent 2px),
            radial-gradient(circle at 40% 60%, #ef4444 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px, 120px 120px, 60px 60px',
          animation: 'pattern-drift 30s linear infinite'
        }}
      />

      {/* Animated African Elements */}
      <motion.div
        className="absolute top-20 left-10 sm:left-20 w-32 h-32 sm:w-64 sm:h-64 rounded-full blur-3xl opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 70%, transparent 100%)'
        }}
        animate={{
          x: [0, 25, 0],
          y: [0, -15, 0],
          scale: [1, 1.1, 1]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 right-10 sm:right-20 w-40 h-40 sm:w-80 sm:h-80 rounded-full blur-3xl opacity-8"
        style={{
          background: 'radial-gradient(circle, rgba(251, 191, 36, 0.25) 0%, rgba(251, 191, 36, 0.1) 70%, transparent 100%)'
        }}
        animate={{
          x: [0, -25, 0],
          y: [0, 15, 0],
          scale: [1, 0.9, 1]
        }}
        transition={{ duration: 10, repeat: Infinity, delay: 2 }}
      />
      <motion.div
        className="absolute top-1/3 right-1/3 w-24 h-24 sm:w-48 sm:h-48 rounded-full blur-2xl opacity-5"
        style={{
          background: 'radial-gradient(circle, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.05) 70%, transparent 100%)'
        }}
        animate={{
          rotate: [0, 360],
          scale: [1, 1.3, 1]
        }}
        transition={{ duration: 15, repeat: Infinity }}
      />

      {/* Main Container - Different layouts for mobile vs desktop */}
      <div className="relative w-full">
        {/* Mobile Layout - Image as Background */}
        <div className="lg:hidden relative min-h-screen flex items-center justify-center">
          {/* Mobile Background Images */}
          {heroSlides.map((slide, index) => (
            <motion.div
              key={index}
              className="absolute inset-0"
              initial={{ opacity: 0 }}
              animate={{
                opacity: index === currentSlideIndex ? 1 : 0
              }}
              transition={{ duration: 1.2 }}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                className="w-full h-full object-cover"
              />
              {/* African-themed mobile overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black/90" />
            </motion.div>
          ))}

          {/* Mobile Content Overlay */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative z-10 w-full max-w-lg mx-auto px-6"
          >
            <div className="text-center">
              {/* Company Tagline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <span className="inline-block px-6 py-3 bg-white/10 backdrop-blur-md text-white border border-white/30 rounded-full text-sm font-semibold mb-6 shadow-lg">
                  Empowering African Voices, Building Global Champions
                </span>
              </motion.div>

              {/* Mobile Heading - Dynamic based on current slide */}
              <motion.h1
                key={currentSlideIndex}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-6"
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-green-300 to-red-300">
                  {currentSlide.subtitle}
                </span>
              </motion.h1>

              {/* Mobile Description - Dynamic */}
              <motion.p
                key={`desc-${currentSlideIndex}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-white/90 text-lg leading-relaxed mb-4"
              >
                {currentSlide.description}
              </motion.p>

              {/* Price for Eagles Club slide */}
              {currentSlide.price && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="mb-6"
                >
                  <span className="inline-block px-4 py-2 bg-yellow-500 text-black rounded-full text-sm font-bold shadow-lg">
                    {currentSlide.price}
                  </span>
                </motion.div>
              )}

              {/* Mobile CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="flex flex-col gap-4 mb-8"
              >
                <Link to={currentSlide.ctaLink}>
                  <button className="btn-african w-full px-8 py-4 rounded-full text-lg font-bold shadow-xl">
                    <FaRocket className="inline mr-2" />
                    {currentSlide.ctaText}
                  </button>
                </Link>
                <Link to="/events">
                  <button className="btn-outline-african w-full px-8 py-4 rounded-full text-lg font-bold border-2 border-white/40 text-white hover:bg-white/10">
                    <FaCalendarAlt className="inline mr-2" />
                    Upcoming Events
                  </button>
                </Link>
              </motion.div>

              {/* Mobile Navigation Dots */}
              <div className="flex justify-center space-x-3">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlideIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${index === currentSlideIndex
                      ? 'bg-yellow-400 w-8 shadow-lg'
                      : 'bg-white/60 hover:bg-white/80'
                      }`}
                    aria-label={`View slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Desktop Layout - Side by Side with Overlapping Text */}
        <div className="hidden lg:block">
          <div className="relative flex flex-col items-center mx-auto lg:flex-row-reverse lg:max-w-5xl xl:max-w-6xl px-4 sm:px-6 lg:px-8">

            {/* Desktop Image Column */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="w-full h-64 lg:w-1/2 lg:h-[500px] xl:h-[600px] relative overflow-hidden rounded-2xl"
            >
              {heroSlides.map((slide, index) => (
                <motion.img
                  key={index}
                  src={slide.src}
                  alt={slide.alt}
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{
                    opacity: index === currentSlideIndex ? 1 : 0,
                    scale: index === currentSlideIndex ? 1 : 1.1
                  }}
                  transition={{
                    opacity: { duration: 1.2 },
                    scale: { duration: 5 }
                  }}
                />
              ))}

              {/* Desktop Navigation Dots */}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-3">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlideIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none ${index === currentSlideIndex
                      ? 'bg-yellow-400 w-8 shadow-md'
                      : 'bg-white/70 hover:bg-white/90'
                      }`}
                    aria-label={`View slide ${index + 1}`}
                  />
                ))}
              </div>

              {/* Desktop Image Title */}
              <div className="absolute top-6 left-6 bg-black/20 backdrop-blur-md rounded-xl px-4 py-2">
                <span className="text-white text-sm font-semibold drop-shadow">
                  {currentSlide.title}
                </span>
              </div>
            </motion.div>

            {/* Desktop Text Column - Overlapping the image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="max-w-lg bg-white md:max-w-2xl md:z-10 md:shadow-xl md:absolute md:top-0 md:mt-32 lg:w-3/5 lg:left-0 lg:mt-20 lg:ml-20 xl:mt-24 xl:ml-12 rounded-2xl"
            >
              {/* Desktop Text Content */}
              <div className="flex flex-col p-8 md:p-12">
                {/* Company Tagline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <span className="inline-block px-4 py-2 bg-green-100 rounded-full border border-green-200 mb-6 text-green-700 text-sm font-medium">
                    Empowering African Voices, Building Global Champions
                  </span>
                </motion.div>

                {/* Desktop Main Heading - Dynamic */}
                <motion.h1
                  key={`desktop-${currentSlideIndex}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-4"
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 via-yellow-600 to-red-600">
                    {currentSlide.subtitle}
                  </span>
                </motion.h1>

                {/* Desktop Description - Dynamic */}
                <motion.p
                  key={`desktop-desc-${currentSlideIndex}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="text-gray-700 text-base leading-relaxed mb-6"
                >
                  {currentSlide.description}
                </motion.p>

                {/* Price for Eagles Club slide */}
                {currentSlide.price && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mb-6"
                  >
                    <span className="inline-block px-4 py-2 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-full text-sm font-bold">
                      {currentSlide.price}
                    </span>
                  </motion.div>
                )}

                {/* Desktop Achievement Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 1.2 }}
                  className="grid grid-cols-2 gap-3 text-sm mb-6"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                    <span className="text-gray-600 font-medium">100+ Schools</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></div>
                    <span className="text-gray-600 font-medium">5+ Countries</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></div>
                    <span className="text-gray-600 font-medium">50+ Championships</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span className="text-gray-600 font-medium">5000+ Students</span>
                  </div>
                </motion.div>

                {/* Desktop CTA Buttons - 3-Column Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 1.4 }}
                  className="grid grid-cols-3 gap-2"
                >
                  <Link to={currentSlide.ctaLink}>
                    <button className="w-full px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-full text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                      <FaRocket className="inline mr-1 text-xs" />
                      {currentSlide.ctaText}
                    </button>
                  </Link>
                  <Link to="/events">
                    <button className="w-full px-3 py-2 border border-gray-300 text-gray-700 rounded-full text-xs font-semibold hover:bg-gray-50 transition-all duration-300">
                      <FaCalendarAlt className="inline mr-1 text-xs" />
                      Events
                    </button>
                  </Link>
                  <Link to="/programs">
                    <button className="w-full px-3 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-full text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-300">
                      <FaGraduationCap className="inline mr-1 text-xs" />
                      Programs
                    </button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Custom hook for number counting animation
const useCountUp = (end, duration = 2000, start = 0) => {
  const [count, setCount] = useState(start);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isActive) return;

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateCount = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * (end - start) + start);

      setCount(currentCount);

      if (now < endTime) {
        requestAnimationFrame(updateCount);
      } else {
        setCount(end);
      }
    };

    const timer = requestAnimationFrame(updateCount);
    return () => cancelAnimationFrame(timer);
  }, [end, duration, start, isActive]);

  return [count, setIsActive];
};

// Stats Section Component - Updated with real data and light theme
const StatsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  const stats = [
    {
      icon: FaUsers,
      number: 100,
      suffix: '+',
      label: 'Schools Impacted',
      color: 'text-green-600',
      bgColor: 'from-green-100 to-green-50',
      iconBg: 'bg-green-500'
    },
    {
      icon: FaGlobe,
      number: 5,
      suffix: '+',
      label: 'Countries Reached',
      color: 'text-blue-600',
      bgColor: 'from-blue-100 to-blue-50',
      iconBg: 'bg-blue-500'
    },
    {
      icon: FaTrophy,
      number: 50,
      suffix: '+',
      label: 'Championships Hosted',
      color: 'text-yellow-600',
      bgColor: 'from-yellow-100 to-yellow-50',
      iconBg: 'bg-yellow-500'
    },
    {
      icon: FaGraduationCap,
      number: 5000,
      suffix: '+',
      label: 'Students Empowered',
      color: 'text-purple-600',
      bgColor: 'from-purple-100 to-purple-50',
      iconBg: 'bg-purple-500'
    }
  ];

  return (
    <section ref={ref} className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative -mt-12">
      {/* Background Pattern 
      <div className="absolute inset-0 opacity-3">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, #22c55e 2px, transparent 2px),
              radial-gradient(circle at 80% 80%, #fbbf24 2px, transparent 2px),
              radial-gradient(circle at 40% 60%, #ef4444 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px, 120px 120px, 60px 60px'
          }}
        />
      </div> */}

      <div className="container mx-auto max-w-6xl relative z-10">
        {/* Minimal Header - matching hero style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 sm:mb-6">
            <span className="block mt-1">
              <span className="text-green-600 drop-shadow-lg">Debate.</span>{' '}
              <span className="text-yellow-600 drop-shadow-lg">Lead.</span>{' '}
              <span className="text-red-600 drop-shadow-lg">Change The World.</span>
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
            Numbers that showcase our commitment to developing globally competitive skills in African children
          </p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <StatCard
                key={index}
                stat={stat}
                index={index}
                inView={inView}
                Icon={Icon}
              />
            );
          })}
        </div>

        {/* Achievement highlights 
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 border border-gray-200">
            <p className="text-gray-700 text-lg sm:text-xl mb-6 sm:mb-10 font-semibold">
              Our Partners
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8 place-items-center">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <img 
                    src={learning} 
                    alt="a learning lab" 
                    className="w-20 h-14 sm:w-32 sm:h-22 lg:w-40 lg:h-28 rounded-lg sm:rounded-xl object-contain border border-gray-300 sm:border-2 shadow-lg sm:shadow-2xl bg-white p-2 sm:p-3 transition-all duration-300 hover:shadow-green-500/50 hover:shadow-2xl hover:scale-105"
                    style={{
                      filter: 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.3)) drop-shadow(0 0 8px rgba(34, 197, 94, 0.2))'
                    }}
                  />
                </div>
                <span className="text-xs sm:text-sm text-gray-600 mt-2 sm:mt-4 font-medium text-center">a learning lab</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="relative">
                  <img 
                    src={unihawk} 
                    alt="Unihawk" 
                    className="w-20 h-14 sm:w-32 sm:h-22 lg:w-40 lg:h-28 rounded-lg sm:rounded-xl object-contain border border-gray-300 sm:border-2 shadow-lg sm:shadow-2xl bg-white p-2 sm:p-3 transition-all duration-300 hover:shadow-blue-500/50 hover:shadow-2xl hover:scale-105"
                    style={{
                      filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3)) drop-shadow(0 0 8px rgba(59, 130, 246, 0.2))'
                    }}
                  />
                </div>
                <span className="text-xs sm:text-sm text-gray-600 mt-2 sm:mt-4 font-medium text-center">Unihawk</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="relative">
                  <img 
                    src={oxford} 
                    alt="Oxford Schools" 
                    className="w-20 h-14 sm:w-32 sm:h-22 lg:w-40 lg:h-28 rounded-lg sm:rounded-xl object-contain border border-gray-300 sm:border-2 shadow-lg sm:shadow-2xl bg-white p-2 sm:p-3 transition-all duration-300 hover:shadow-purple-500/50 hover:shadow-2xl hover:scale-105"
                    style={{
                      filter: 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.3)) drop-shadow(0 0 8px rgba(147, 51, 234, 0.2))'
                    }}
                  />
                </div>
                <span className="text-xs sm:text-sm text-gray-600 mt-2 sm:mt-4 font-medium text-center">Oxford Schools</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="relative">
                  <img 
                    src={toloita} 
                    alt="Toloita Tech" 
                    className="w-20 h-14 sm:w-32 sm:h-22 lg:w-40 lg:h-28 rounded-lg sm:rounded-xl object-contain border border-gray-300 sm:border-2 shadow-lg sm:shadow-2xl bg-white p-2 sm:p-3 transition-all duration-300 hover:shadow-purple-500/50 hover:shadow-2xl hover:scale-105"
                    style={{
                      filter: 'drop-shadow(0 0 4px rgba(147, 51, 234, 0.3)) drop-shadow(0 0 8px rgba(147, 51, 234, 0.2))'
                    }}
                  />
                </div>
                <span className="text-xs sm:text-sm text-gray-600 mt-2 sm:mt-4 font-medium text-center">Toloita Technologies</span>
              </div>
            </div>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed px-4">
              From Nairobi to the world - Building globally competitive African children through debate and arts
            </p>
          </div>
        </motion.div>*/}
      </div>
    </section>
  );
};

// Updated StatCard Component - More mobile friendly
const StatCard = ({ stat, index, inView, Icon }) => {
  const [count, setIsActive] = useCountUp(stat.number, 2500 + (index * 300));

  useEffect(() => {
    if (inView) {
      const timer = setTimeout(() => {
        setIsActive(true);
      }, (index * 200) + 400); // Consistent with hero section timing

      return () => clearTimeout(timer);
    }
  }, [inView, index, setIsActive]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{
        duration: 0.8,
        delay: (index * 0.15) + 0.4, // Consistent with hero section pattern
        type: "spring",
        stiffness: 100
      }}
      className="group w-full"
    >
      <div className={`bg-gradient-to-br ${stat.bgColor} rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 text-center transition-all duration-500 hover:scale-105 hover:shadow-xl border border-gray-100 group-hover:border-gray-200 h-full flex flex-col justify-center min-h-[140px] sm:min-h-[180px]`}>
        
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 0.5, delay: (index * 0.15) + 0.7 }}
          className="relative mx-auto mb-3 sm:mb-6"
        >
          <div className={`${stat.iconBg} w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto shadow-lg transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
          </div>
        </motion.div>

        {/* Number */}
        <motion.div
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 0.5, delay: (index * 0.15) + 0.9 }}
          className={`text-2xl sm:text-4xl lg:text-5xl font-bold mb-1 sm:mb-2 ${stat.color}`}
        >
          {count.toLocaleString()}{stat.suffix}
        </motion.div>

        {/* Label */}
        <p className="text-gray-700 font-medium text-xs sm:text-sm lg:text-lg leading-tight">
          {stat.label}
        </p>
      </div>
    </motion.div>
  );
};
// Features Section Component - Mobile Fixed Version
const FeaturesSection = ({ navigate, isSignedIn }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1, // Reduced threshold for mobile
    rootMargin: '-50px 0px' // Better mobile detection
  });

  // Handle button clicks
  const handleEnrollStudents = () => {
    if (!isSignedIn) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }

    // Note: User type check removed - now handled on backend/events page
    // Redirect to events page
    navigate('/events');
  };

  const handleJoinEaglesClub = () => {
    // Redirect to programs page - user can search for Eagles Club there
    navigate('/programs');
  };

  const handleJoinAIthon = () => {
    // Redirect to events page to check for AIthons
    navigate('/events');
  };

  const features = [
    {
      icon: FaRocket,
      title: 'EAGLES CLUB',
      subtitle: 'Elite Premium Training Program',
      description: 'Exclusive premium program combining all ACDC offerings with boot camp training during holidays and international opportunities.',
      color: 'text-yellow-700',
      bgColor: 'from-yellow-50 to-yellow-100',
      iconBg: 'bg-yellow-500',
      span: 'md:col-span-2'
    },
    {
      icon: FaMicrophone,
      title: 'Debate Training',
      description: 'Training students to think critically, articulate their thoughts, and develop persuasive arguments through structured debate programs.',
      color: 'text-green-700',
      bgColor: 'from-green-50 to-green-100',
      iconBg: 'bg-green-500'
    },
    {
      icon: FaUsers,
      title: 'Public Speaking',
      description: 'Developing confident, impactful communicators who can express themselves clearly and persuasively on any stage.',
      color: 'text-blue-700',
      bgColor: 'from-blue-50 to-blue-100',
      iconBg: 'bg-blue-500'
    },
    {
      icon: FaBookOpen,
      title: 'Spelling Bee',
      description: 'Enhancing vocabulary, focus, and mental agility through competitive spelling programs and word mastery challenges.',
      color: 'text-purple-700',
      bgColor: 'from-purple-50 to-purple-100',
      iconBg: 'bg-purple-500'
    },
    {
      icon: FaQuoteLeft,
      title: 'Storytelling',
      description: 'Nurturing creativity and cultural pride through narratives that celebrate African heritage and universal themes.',
      color: 'text-orange-700',
      bgColor: 'from-orange-50 to-orange-100',
      iconBg: 'bg-orange-500'
    },
    {
      icon: FaStar,
      title: 'Art Programs',
      description: 'Encouraging self-expression through visual and performing arts, fostering creativity and artistic development.',
      color: 'text-pink-700',
      bgColor: 'from-pink-50 to-pink-100',
      iconBg: 'bg-pink-500'
    },
    {
      icon: FaLaptopCode,
      title: 'Hackathons',
      description: 'Innovation-focused technology competitions where students collaborate to solve real-world problems using coding and creative thinking.',
      color: 'text-indigo-700',
      bgColor: 'from-indigo-50 to-indigo-100',
      iconBg: 'bg-indigo-500'
    },
    {
      icon: FaCode,
      title: 'AIthon',
      description: 'Specialized AI competitions where students build intelligent solutions using artificial intelligence and machine learning technologies.',
      color: 'text-cyan-700',
      bgColor: 'from-cyan-50 to-cyan-100',
      iconBg: 'bg-cyan-500'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
      <div className="container mx-auto max-w-7xl">
        {/* Mobile Header - Always Visible */}
        <div className="text-center lg:hidden mb-12">
          <div className="inline-block px-4 py-2 bg-blue-100 rounded-full border border-blue-200 mb-6">
            <span className="text-blue-700 text-sm font-medium">Our Programs</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-6">
            <span className="text-gray-900">Comprehensive Programs for</span>{' '}
            <span className="block mt-1">
              <span className="text-green-600 drop-shadow-sm">Debate.</span>{' '}
              <span className="text-yellow-600 drop-shadow-sm">Arts.</span>{' '}
              <span className="text-indigo-600 drop-shadow-sm">Innovation.</span>
            </span>
          </h2>

          <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            Eight specialized programs designed to develop globally competitive skills while celebrating African culture and fostering technological innovation.
          </p>

          {/* Achievement badges - Mobile */}
          <div className="flex flex-wrap gap-3 justify-center mb-8">
            <div className="flex items-center bg-white rounded-2xl px-3 py-2 shadow-sm border">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-700 font-medium text-sm">8 Core Programs</span>
            </div>
            <div className="flex items-center bg-white rounded-2xl px-3 py-2 shadow-sm border">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-gray-700 font-medium text-sm">Expert Training</span>
            </div>
            <div className="flex items-center bg-white rounded-2xl px-3 py-2 shadow-sm border">
              <div className="w-3 h-3 bg-indigo-500 rounded-full mr-2"></div>
              <span className="text-gray-700 font-medium text-sm">Tech Innovation</span>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Header */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-left sticky top-20"
          >
            <div className="inline-block px-4 py-2 bg-blue-100 rounded-full border border-blue-200 mb-6">
              <span className="text-blue-700 text-sm font-medium">Our Programs</span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              <span className="text-gray-900">Comprehensive Programs for</span>{' '}
              <span className="block mt-1">
                <span className="text-green-600 drop-shadow-sm">Debate.</span>{' '}
                <span className="text-yellow-600 drop-shadow-sm">Arts.</span>{' '}
                <span className="text-indigo-600 drop-shadow-sm">Innovation.</span>
              </span>
            </h2>

            <p className="text-xl text-gray-600 max-w-lg leading-relaxed mb-8">
              Eight specialized programs designed to develop globally competitive skills while celebrating African culture and fostering technological innovation.
            </p>

            {/* Achievement badges - Desktop */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center bg-white rounded-2xl px-4 py-2 shadow-sm border">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">8 Core Programs</span>
              </div>
              <div className="flex items-center bg-white rounded-2xl px-4 py-2 shadow-sm border">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">Expert Training</span>
              </div>
              <div className="flex items-center bg-white rounded-2xl px-4 py-2 shadow-sm border">
                <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
                <span className="text-gray-700 font-medium">Tech Innovation</span>
              </div>
            </div>

            {/* CTA Buttons - Desktop Only */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleEnrollStudents}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg text-left"
              >
                <FaGraduationCap className="inline mr-2" />
                Enroll Your Students
              </button>
              <button
                onClick={handleJoinEaglesClub}
                className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg text-left inline-block"
              >
                <FaRocket className="inline mr-2" />
                Join EAGLES CLUB
              </button>
            </div>
          </motion.div>

          {/* Right Column - Programs Grid - Desktop */}
          <div className="relative" ref={ref}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{
                      duration: 0.8,
                      delay: index * 0.1,
                      type: "spring",
                      stiffness: 100
                    }}
                    className={`group ${feature.span || ''}`}
                  >
                    <div className={`bg-gradient-to-br ${feature.bgColor} rounded-3xl p-6 transform transition-all duration-500 hover:scale-105 hover:-rotate-1 border border-white/50 shadow-lg hover:shadow-xl h-full`}>
                      <div className="mb-4">
                        <div className={`${feature.iconBg} w-14 h-14 rounded-2xl flex items-center justify-center shadow-md transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                      </div>

                      <h3 className={`text-lg font-bold ${feature.color} mb-2 group-hover:text-gray-900 transition-colors`}>
                        {feature.title}
                      </h3>

                      {feature.subtitle && (
                        <h4 className="text-sm font-semibold text-gray-600 mb-3">
                          {feature.subtitle}
                        </h4>
                      )}

                      <p className="text-gray-700 text-sm leading-relaxed mb-4">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile Programs Grid - Always Visible */}
        <div className="lg:hidden">
          <div className="grid grid-cols-1 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <div
                  key={index}
                  className="group"
                >
                  <div className={`bg-gradient-to-br ${feature.bgColor} rounded-3xl p-6 border border-white/50 shadow-lg h-full`}>
                    <div className="mb-4">
                      <div className={`${feature.iconBg} w-12 h-12 rounded-2xl flex items-center justify-center shadow-md`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    <h3 className={`text-lg font-bold ${feature.color} mb-2`}>
                      {feature.title}
                    </h3>

                    {feature.subtitle && (
                      <h4 className="text-sm font-semibold text-gray-600 mb-3">
                        {feature.subtitle}
                      </h4>
                    )}

                    <p className="text-gray-700 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom section with CTA */}
        <div className="mt-16 sm:mt-20 text-center">
          <div className="bg-gradient-to-r from-green-50 to-indigo-50 rounded-3xl p-8 sm:p-12 border border-gray-200">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Ready to Empower Your Students?
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Join our comprehensive programs and watch your students develop into confident, articulate leaders ready to change the world through words and technology.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={handleEnrollStudents}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg"
              >
                <FaGraduationCap className="inline mr-2" />
                Enroll Your Students
              </button>
              <button
                onClick={handleJoinEaglesClub}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-yellow-500 to-amber-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg inline-block"
              >
                <FaRocket className="inline mr-2" />
                Join EAGLES CLUB
              </button>
              <button
                onClick={handleJoinAIthon}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg"
              >
                <FaCode className="inline mr-2" />
                Join AIthon
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Fixed Upcoming Events Section - Clean version with minimal icons
const UpcomingEventsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userEvents, setUserEvents] = useState([]); // Track user's registered events
  const { isSignedIn, isLoaded } = useUser();
  const navigate = useNavigate();

  // Fetch all events (not filtered by registration status)
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        
        // CRITICAL FIX: Remove all filters that might hide registered events
        // Just get ALL events, don't filter by upcoming, featured, or anything else
        const data = await eventsAPI.getAll({
          // Remove all filters that might cause issues
          limit: 10 // Get more events
        });
        
        let allEvents = [];
        if (data.success) {
          allEvents = data.events || [];
        } else if (Array.isArray(data)) {
          allEvents = data;
        }

        setEvents(allEvents);

        // If user is authenticated, also fetch their registered events
        if (isSignedIn) {
          try {
            const userEventsData = await eventsAPI.getUserEvents();
            
            let registeredEvents = [];
            if (userEventsData.success) {
              registeredEvents = userEventsData.events || [];
            } else if (Array.isArray(userEventsData)) {
              registeredEvents = userEventsData;
            }

            setUserEvents(registeredEvents);

            // IMPORTANT: Make sure registered events are included in the main events list
            // In case the API excludes them, we'll add them back
            const registeredEventIds = registeredEvents.map(e => e._id || e.eventId);
            const missingRegisteredEvents = registeredEvents.filter(regEvent => 
              !allEvents.some(event => event._id === (regEvent._id || regEvent.eventId))
            );

            if (missingRegisteredEvents.length > 0) {
              setEvents(prev => [...prev, ...missingRegisteredEvents]);
            }

          } catch (error) {
            setUserEvents([]);
          }
        }
        
      } catch (error) {
        toast.error('Failed to load events');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [isSignedIn]); // Re-fetch when auth status changes

  // Check if user is registered for a specific event
  const isUserRegistered = (eventId) => {
    return userEvents.some(userEvent => userEvent._id === eventId || userEvent.eventId === eventId);
  };

  // Navigate to event details page
  const handleEventClick = (eventId, e) => {
    // Prevent navigation if clicking on the register button
    if (e.target.closest('.register-button')) {
      return;
    }
    navigate(`/events/${eventId}`);
  };

  const handleRegister = async (eventId, eventTitle, e) => {
    e.stopPropagation(); // Prevent event card click navigation
    
    if (!isSignedIn) {
      toast.error('Please login to register for events');
      setTimeout(() => {
        navigate('/login');
      }, 1000);
      return;
    }

    // Check if already registered
    if (isUserRegistered(eventId)) {
      toast.info('You are already registered for this event!');
      navigate('/dashboard');
      return;
    }
    
    try {
      const response = await eventsAPI.register(eventId);
      
      if (response.success) {
        toast.success('Successfully registered for event!');
        
        // Update userEvents to reflect the new registration
        const registeredEvent = events.find(event => event._id === eventId);
        if (registeredEvent) {
          setUserEvents(prev => [...prev, registeredEvent]);
        }
        
        // Navigate to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to register for event');
    }
  };

  const EventCard = ({ event, index }) => {
    const isRegistered = isUserRegistered(event._id);
    const ended = isEventPast(event);
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
        transition={{
          duration: 0.8,
          delay: index * 0.1,
          type: "spring",
          stiffness: 100
        }}
        className="group h-full cursor-pointer"
        onClick={(e) => handleEventClick(event._id, e)}
      >
        <div className={`bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border h-full flex flex-col group-hover:border-gray-300 ${
          isRegistered ? 'border-green-300 bg-green-50/30' : 'border-gray-100'
        }`}>
          {/* Image Container */}
          <div className="h-48 relative overflow-hidden flex-shrink-0">
            <img
              src={event.thumbnail || event.image || 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=400&h=250&fit=crop'}
              alt={event.title}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

            {/* Registration Status Badge */}
            {ended ? (
              <span className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center">
                ENDED
              </span>
            ) : isRegistered ? (
              <span className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                REGISTERED
              </span>
            ) : event.featured && (
              <span className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                FEATURED
              </span>
            )}

            {/* Event Type Badge */}
            <span className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold ${
              event.type === 'tournament' ? 'bg-purple-500 text-white' :
              event.type === 'workshop' ? 'bg-blue-500 text-white' :
              event.type === 'training' ? 'bg-orange-500 text-white' :
              'bg-gray-800 text-white'
            }`}>
              {event.type?.toUpperCase() || 'EVENT'}
            </span>

            {/* Date Badge */}
            <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-full">
              <span className="text-white text-sm font-semibold">
                {event.date ? new Date(event.date).toLocaleDateString() : 'TBA'}
              </span>
            </div>
          </div>

          {/* Content Container */}
          <div className="p-6 flex-1 flex flex-col">
            {/* Title */}
            <h3 className={`text-xl font-bold mb-2 line-clamp-2 transition-colors ${
              isRegistered ? 'text-green-700' : 'text-gray-900 group-hover:text-green-600'
            }`}>
              {event.title}
            </h3>

            {/* Location & Time */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-600 text-sm font-medium">{event.location || 'Location TBA'}</span>
              <span className="text-gray-500 text-sm">{event.time || 'Time TBA'}</span>
            </div>

            {/* Description */}
            <p className="text-gray-700 text-sm mb-4 line-clamp-3 leading-relaxed flex-1">
              {event.description || 'Join us for an exciting debate competition that will challenge your skills and expand your horizons.'}
            </p>

            {/* Event Details */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                  {event.difficulty || 'All Levels'}
                </span>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {event.participantCount || 0}/{event.maxParticipants || 100} spots
                </span>
                {event.prize && (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                    Prize: {event.prize}
                  </span>
                )}
              </div>
            </div>

            {/* Simplified Stats - Removed excessive icons */}
            <div className="flex items-center justify-between mb-4">
              <div className="text-gray-600 text-sm">
                Rating: {event.rating || '5.0'}/5
              </div>
              <div className="text-gray-600 text-sm">
                {event.likes || Math.floor(Math.random() * 1000)} interested
              </div>
            </div>

            {/* Action Button */}
            <button 
              onClick={(e) => handleRegister(event._id, event.title, e)}
              disabled={isRegistered || ended}
              className={`register-button w-full py-3 rounded-2xl font-semibold transition-all duration-300 hover:shadow-lg flex items-center justify-center ${
                (ended ? 'bg-gray-200 text-gray-600 cursor-not-allowed' : (
                  isRegistered 
                    ? 'bg-green-100 text-green-700 border-2 border-green-300 cursor-default hover:scale-100'
                    : isSignedIn 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white hover:scale-105'
                      : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white hover:scale-105'
                ))
              }`}
            >
              <FaCalendarAlt className="inline mr-2" />
              {ended ? 'Event Ended' : (isRegistered ? 'Already Registered ✓' : (isSignedIn ? 'Register Now' : 'Login to Register'))}
            </button>

            {/* Additional info for registered users */}
            {isRegistered && (
              <p className="text-center text-xs text-green-600 mt-2 font-medium">
                Check your dashboard for event details and updates
              </p>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <section ref={ref} className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-4 py-2 bg-orange-100 rounded-full border border-orange-200 mb-6">
            <span className="text-orange-700 text-sm font-medium">Upcoming Events 2025</span>
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
            <span className="text-gray-900">Exciting Events Coming</span>{' '}
            <span className="block mt-1">
              <span className="text-green-600 drop-shadow-sm">This</span>{' '}
              <span className="text-yellow-600 drop-shadow-sm">Year.</span>{' '}
              <span className="text-red-600 drop-shadow-sm">Join Us!</span>
            </span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From local festivals to global championships - opportunities to showcase your skills and compete with the best young minds across Africa and beyond
          </p>

          {/* Show registration status if user is authenticated */}
          {isSignedIn && userEvents.length > 0 && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl max-w-md mx-auto">
              <p className="text-green-700 font-medium">
                You're registered for {userEvents.length} event{userEvents.length !== 1 ? 's' : ''}
              </p>
              <p className="text-green-600 text-sm">
                Events you've registered for are marked with a green checkmark
              </p>
            </div>
          )}
        </motion.div>

        {/* Events Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-3xl border border-gray-200 overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-20 bg-gray-200 rounded mb-4"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event, index) => (
              <EventCard key={event._id} event={event} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FaCalendarAlt className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Events Available</h3>
            <p className="text-gray-500">Check back soon for exciting upcoming events!</p>
          </div>
        )}

        {/* Bottom CTA section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="text-center mt-20"
        >
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-12 border border-gray-200">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Join the Next Generation of Leaders?
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              These events fill up fast! Secure your spot and be part of Africa's most prestigious debate and leadership community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/events">
                <button className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg">
                  <FaCalendarAlt className="inline mr-2" />
                  View All Events
                </button>
              </Link>
              <Link to={isSignedIn ? "/events" : "/register"}>
                <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-2xl font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-300">
                  <FaRocket className="inline mr-2" />
                  {isSignedIn ? "Explore Events" : "Join EAGLES CLUB Priority"}
                </button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Updated GallerySection Component - Fetches from Backend
const GallerySection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch gallery images from backend
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getGallery();
        
        if (response.success && response.gallery && response.gallery.length > 0) {
          // Take only the first 5 images for the home page carousel
          const limitedGallery = response.gallery.slice(0, 5).map(item => ({
            image: item.imageUrl,
            title: item.title || '',
            category: item.category || ''
          }));
          setGalleryItems(limitedGallery);
        } else {
          // Fallback to assets if no backend images
          setGalleryItems([
            { image: assets.gallery.awards[0] },
            { image: assets.gallery.competitions[0] },
            { image: assets.gallery.events[0] },
            { image: assets.gallery.moments[0] },
            { image: assets.gallery.officials[0] }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
        // Fallback to assets on error
        setGalleryItems([
          { image: assets.gallery.awards[0] },
          { image: assets.gallery.competitions[0] },
          { image: assets.gallery.events[0] },
          { image: assets.gallery.moments[0] },
          { image: assets.gallery.officials[0] }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);


  const totalItems = galleryItems.length;

  // Auto-advance every 4 seconds
  useEffect(() => {
    if (totalItems === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % totalItems);
    }, 4000);

    return () => clearInterval(interval);
  }, [totalItems]);

  const handleNavigation = (direction) => {
    setCurrentIndex(prev => {
      const newIndex = prev + direction;
      return ((newIndex % totalItems) + totalItems) % totalItems;
    });
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Show loading state
  if (loading) {
    return (
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10">
            <div className="inline-block px-3 py-1 bg-purple-100 rounded-full mb-4">
              <span className="text-purple-700 text-sm font-medium">Gallery</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">
              <span className="text-gray-900">Our Journey of</span>{' '}
              <span className="block mt-1">
                <span className="text-green-600">Success.</span>{' '}
                <span className="text-yellow-600">Growth.</span>{' '}
                <span className="text-red-600">Impact.</span>
              </span>
            </h2>
          </div>
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        </div>
      </section>
    );
  }

  // Don't render if no gallery items
  if (galleryItems.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref}
      className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50"
    >
      <div className="container mx-auto max-w-6xl">
        {/* Clean Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <div className="inline-block px-3 py-1 bg-purple-100 rounded-full mb-4">
            <span className="text-purple-700 text-sm font-medium">Gallery</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">
            <span className="text-gray-900">Our Journey of</span>{' '}
            <span className="block mt-1">
              <span className="text-green-600">Success.</span>{' '}
              <span className="text-yellow-600">Growth.</span>{' '}
              <span className="text-red-600">Impact.</span>
            </span>
          </h2>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Witness the transformative power of debate and leadership training
          </p>
        </motion.div>

        {/* Clean Gallery Layout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-5xl mx-auto"
        >
          {/* Main Image Display - Clean, no overlays */}
          <div className="relative mb-6">
            <div className="aspect-[3/1] relative overflow-hidden rounded-2xl bg-white shadow-xl border border-gray-200">
              <motion.img
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                src={galleryItems[currentIndex].image}
                alt={galleryItems[currentIndex].title || `Gallery image ${currentIndex + 1}`}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => handleNavigation(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-105"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={() => handleNavigation(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-105"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Thumbnail Navigation */}
          <div className="flex justify-center space-x-3 mb-4">
            {galleryItems.map((item, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                  index === currentIndex
                    ? 'border-green-500 shadow-md scale-105'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <img
                  src={item.image}
                  alt={item.title || `Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center space-x-2 mb-6">
            {galleryItems.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-green-500 scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Link to="/gallery">
              <button className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:scale-105 transition-all duration-300 hover:shadow-lg text-sm">
                <FaImages className="inline mr-2" />
                View Full Gallery
              </button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section Component - Compact version with uniform mobile height
const TestimonialsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const handleViewSuccessStories = () => {
    // Navigate to contact page for now - will be updated when blog feature is added
    toast.info('Success Stories blog feature coming soon! Contact us to learn more about our student achievements.');
    // Optional: Could navigate to /contact instead
    // window.location.href = '/contact';
  };

  const testimonials = [
    {
      name: 'Aisha Kimani',
      school: 'Makini Schools, Nairobi',
      role: 'EAGLES CLUB Graduate',
      content: 'ACD transformed my confidence and public speaking skills completely. The international exposure has been incredible - from shy student to championship winner!'
    },
    {
      name: 'David Mwangi',
      school: 'Riara Group of Schools',
      role: 'Debate Captain',
      content: 'The quality of coaching and competitive environment helped me develop critical thinking skills that benefit me in all subjects. Best decision ever!'
    },
    {
      name: 'Grace Wanjiku',
      school: 'Green Hills Academy',
      role: 'Public Speaking Champion',
      content: 'Participating in ACD programs opened doors to international competitions I never thought possible. Thank you for believing in us!'
    }
  ];

  return (
    <section ref={ref} className="px-4 sm:px-6 lg:px-8 py-12 bg-white relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 to-purple-50/30"></div>

      <div className="container mx-auto max-w-7xl relative">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">

          {/* Left Column - Header */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="lg:col-span-1"
          >
            <div className="lg:sticky lg:top-8">
              <div className="inline-block px-3 py-1 bg-green-100 rounded-full border border-green-200 mb-4">
                <span className="text-green-700 text-sm font-medium">Student Success Stories</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-4">
                <span className="text-gray-900">What Our Students Say About</span>{' '}
                <span className="block mt-1">
                  <span className="text-green-600 drop-shadow-sm">Debate.</span>{' '}
                  <span className="text-yellow-600 drop-shadow-sm">Growth.</span>{' '}
                  <span className="text-red-600 drop-shadow-sm">Success.</span>
                </span>
              </h2>

              <p className="text-gray-600 text-base mb-6 leading-relaxed">
                Real stories from students who transformed their lives through our world-class debate and leadership programs
              </p>

              <motion.button
                onClick={handleViewSuccessStories}
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 transition-colors group bg-green-50 px-4 py-2 rounded-xl hover:bg-green-100"
              >
                View All Success Stories
                <svg className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
            </div>
          </motion.div>

          {/* Right Column - Testimonials Grid */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 grid-rows-2">

              {/* Featured testimonial - spans both rows */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0 }}
                className="md:row-span-2 flex items-center"
              >
                <div className="bg-white border-2 border-gray-200 hover:border-green-200 rounded-2xl p-6 w-full shadow-lg hover:shadow-xl transition-all duration-300 group min-h-[280px] md:min-h-[320px] flex flex-col justify-center">
                  {/* Student Photo */}
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-lg">{testimonials[0].name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                  </div>

                  {/* Stars */}
                  <div className="flex items-center justify-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
                      </svg>
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-base text-gray-700 leading-relaxed mb-6 text-center italic">
                    "{testimonials[0].content}"
                  </blockquote>

                  {/* Author */}
                  <div className="text-center">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">
                      {testimonials[0].name}
                    </h4>
                    <p className="text-green-600 text-sm font-semibold mb-1">{testimonials[0].role}</p>
                    <p className="text-gray-500 text-xs">{testimonials[0].school}</p>
                  </div>
                </div>
              </motion.div>

              {/* Top right testimonial */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className="bg-gray-50 border border-gray-200 hover:border-blue-200 rounded-xl p-4 h-full shadow-md hover:shadow-lg transition-all duration-300 group min-h-[135px] md:min-h-[155px] flex flex-col justify-between">
                  {/* Student Initial */}
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">{testimonials[1].name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
                        </svg>
                      ))}
                    </div>
                  </div>

                  {/* Quote */}
                  <blockquote className="text-sm text-gray-700 leading-relaxed mb-3 italic flex-1">
                    "{testimonials[1].content}"
                  </blockquote>

                  {/* Author */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-0.5">
                      {testimonials[1].name}
                    </h4>
                    <p className="text-blue-600 text-xs font-semibold mb-0.5">{testimonials[1].role}</p>
                    <p className="text-gray-500 text-xs">{testimonials[1].school}</p>
                  </div>
                </div>
              </motion.div>

              {/* Bottom right testimonial */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="bg-gray-50 border border-gray-200 hover:border-purple-200 rounded-xl p-4 h-full shadow-md hover:shadow-lg transition-all duration-300 group min-h-[135px] md:min-h-[155px] flex flex-col justify-between">
                  {/* Student Initial */}
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-400 to-pink-500 flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">{testimonials[2].name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
                        </svg>
                      ))}
                    </div>
                  </div>

                  {/* Quote */}
                  <blockquote className="text-sm text-gray-700 leading-relaxed mb-3 italic flex-1">
                    "{testimonials[2].content}"
                  </blockquote>

                  {/* Author */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-0.5">
                      {testimonials[2].name}
                    </h4>
                    <p className="text-purple-600 text-xs font-semibold mb-0.5">{testimonials[2].role}</p>
                    <p className="text-gray-500 text-xs">{testimonials[2].school}</p>
                  </div>
                </div>
              </motion.div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


// CTA Section Component - Compact version
const CTASection = ({ isSignedIn, navigate }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  // Handle button clicks
  const handleEnrollChild = () => {
    if (!isSignedIn) {
      navigate('/register');
    } else {
      navigate('/events');
    }
  };

  const handlePartnerWithUs = () => {
    navigate('/contact');
  };

  const handleJoinEaglesClub = () => {
    navigate('/programs');
  };

  const handleViewPrograms = () => {
    navigate('/programs');
  };

  return (
    <section ref={ref} className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 border-2 border-gray-200 rounded-3xl p-8 shadow-lg">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="mb-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">
                <span className="text-gray-900">Ready to Shape Africa's Future Through</span>{' '}
                <span className="block mt-1">
                  <span className="text-green-600 drop-shadow-sm">Debate.</span>{' '}
                  <span className="text-yellow-600 drop-shadow-sm">Leadership.</span>{' '}
                  <span className="text-red-600 drop-shadow-sm">Excellence.</span>
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Join the movement that's shaping the next generation of African leaders.
                Your journey to excellence starts here.
              </p>
            </motion.div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-6">
              {!isSignedIn ? (
                <>
                  <button
                    onClick={handleEnrollChild}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 hover:shadow-xl min-w-[180px] flex items-center justify-center"
                  >
                    <svg className="mr-2 w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l2.4 7.4h7.6l-6 4.6 2.3 7-6.3-4.6-6.3 4.6 2.3-7-6-4.6h7.6z" />
                    </svg>
                    Enroll Your Child Today
                  </button>

                  <button
                    onClick={handlePartnerWithUs}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-base hover:border-green-400 hover:bg-green-50 hover:text-green-700 transition-all duration-300 min-w-[180px] flex items-center justify-center"
                  >
                    <svg className="mr-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Partner With Us
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleJoinEaglesClub}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 hover:shadow-xl min-w-[180px] flex items-center justify-center"
                  >
                    <svg className="mr-2 w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14l9-5-9-5-9 5 9 5z" />
                      <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                    </svg>
                    Join EAGLES CLUB
                  </button>

                  <button
                    onClick={handleViewPrograms}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold text-base hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-all duration-300 min-w-[180px] flex items-center justify-center"
                  >
                    <svg className="mr-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    View Programs
                  </button>
                </>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
              className="pt-6 border-t border-gray-200"
            >
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-3">
                <div className="flex items-center">
                  <span className="text-base font-bold text-green-600 mr-2">100+</span>
                  <span className="text-gray-600 text-sm">Schools Impacted</span>
                </div>
                <div className="flex items-center">
                  <span className="text-base font-bold text-blue-600 mr-2">5000+</span>
                  <span className="text-gray-600 text-sm">Students Empowered</span>
                </div>
                <div className="flex items-center">
                  <span className="text-base font-bold text-purple-600 mr-2">5+</span>
                  <span className="text-gray-600 text-sm">Countries Reached</span>
                </div>
              </div>

              <p className="text-gray-500 text-sm">
                Jewel Complex next to TRM Mall on Thika Rd, 4th Floor Office No. 413. • <span className="font-medium">info@africandebatechampions.org</span> • +254 718 290 692
              </p>
              {/* Sponsors/Partners moved here per request */}
              <SponsorsSection />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Sponsors / Partners Section
const SponsorsSection = () => {
  const [sponsors, setSponsors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const res = await fetchSponsors();
      if (!mounted) return;
      if (res && res.success) {
        // Deduplicate sponsors by _id or name (defensive)
        const list = res.sponsors || [];
        const unique = Array.from(new Map(list.map(s => [(s._id || s.name || Math.random()), s])).values());

        // Remove sponsors that are included in the upper hard-coded partners list to avoid duplication
        const highlighted = ['a learning lab', 'unihawk', 'oxford schools', 'toloita technologies'];
        const filtered = unique.filter(s => {
          const name = (s.name || '').toString().toLowerCase().trim();
          return !highlighted.includes(name);
        });

        setSponsors(filtered);
      }
      setLoading(false);
    };
    load();
    return () => { mounted = false; };
  }, []);

  if (loading) return (
    <section className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center text-gray-600">Loading partners...</div>
      </div>
    </section>
  );

  if (!sponsors || sponsors.length === 0) return null;

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Our Partners</h2>
        <p className="text-center text-gray-600 mb-8">We’re proud to work with organisations that support our mission.</p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 items-start">
          {sponsors.map((s) => (
            <a key={s._id || s.name} href={s.website || '#'} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-4 bg-white border border-gray-100 rounded-2xl hover:shadow-lg transition-all text-center">
              {s.logo ? (
                <img src={s.logo} alt={s.name} className="max-h-16 object-contain mb-3" />
              ) : (
                <div className="text-sm font-medium text-gray-700 mb-3">{s.name}</div>
              )}
              <div className="text-xs text-gray-600 leading-tight line-clamp-2">{s.description || s.notes || ''}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
export default Home;
